<script lang="ts" setup>
import { useI18n } from '#i18n'
import { parseISO } from 'date-fns'
import { fmtMSS } from '~/const/conversion'
import { getWorkByOldId } from '~/queries/work'

const { goToResource } = useGoToResource()
const handleHtmlClick = useHandleHtmlClick()
const { locale } = useI18n()
const { formatToFullDate } = useFullDateFormater()
const { slug } = useResourceSlug()
const { data, pending, error } = await useAsyncData(
  `/works/work/${slug}/`,
  () => execQuery(getWorkByOldId, { oldId: slug })
)

const relatedContent = computed(() => {
  if (pending.value || error.value) return []
  const suggestions = []
  for (let suggestion of data.value.data.workByOldId.suggestions) {
    suggestions.push({
      title: suggestion.cardTitle,
      type: suggestion.__typename,
      description: suggestion.cardDescription,
      image: suggestion.cardImage,
      tags: suggestion.tags,
    })
  }
  return suggestions
})

const simili = computed(() => {
  if (pending.value || error.value) return []
  const simili = []
  for (let s in data.value.data.workByOldId.simili) {
    simili.push({
      title: data.value.data.workByOldId?.simili[s].cardTitle,
      type: data.value.data.workByOldId?.simili[s].__typename,
      description: data.value.data.workByOldId?.simili[s].cardDescription,
      image: data.value.data.workByOldId?.simili[s].cardImage,
      tags: data.value.data.workByOldId?.simili[s].tags,
    })
  }
  return simili
})

const work = computed<Work>(() => {
  if (pending.value || error.value) {
    return {
      commission: null,
      staff: [],
      composers: [],
      documents: [],
      compositionYear: null,
      dedicatee: null,
      libretto: null,
      duration: null,
      durationMax: null,
      kind: null,
      kindEn: null,
      medias: [],
      observation: null,
      performers: null,
      publisher: null,
      parts: null,
      relatedContent: [],
      simili: [],
      tags: [],
    }
  }
  const { workByOldId } = data.value?.data
  const {
    parts,
    composers,
    documents,
    style,
    styleEn,
    styleSlug,
    observation,
    tags,
    duration,
    durationMax,
    editor,
    medias,
  } = workByOldId
  data.value = {
    ...workByOldId,
    editor,
    duration,
    durationMax,
    composers,
    kind: style,
    kindEn: styleEn,
    kindSlug: styleSlug,
    observation,
    medias,
    relatedContent: relatedContent.value,
    simili: simili.value,
    tags,
    parts,
    documents,
  }

  return data.value
})

const title = computed(() => {
  return work.value?.title
})

const description = computed(() => {
  return work.value?.description
})

useHead({
  title,
  meta: [
    {
      name: 'description',
      content: description,
    },
  ],
})

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  twitterCard: 'summary',
})
const { t } = useI18n()

const compositionDate = computed(() => {
  let value
  let start = work.value.publishingStartDate
  let end = work.value.publishingEndDate
  if (start) {
    start = new Date(start).getFullYear()
    value = `${start}`
    if (end) {
      end = new Date(end).getFullYear()
      if (end !== start) {
        value += `-${end}`
      }
    }
  }
  return value
})

const detailedstaff = computed<DescriptionList>(() => {
  let solo
  let others
  if (locale.value === 'fr') {
    solo = work.value.staff?.soloFr
    others = work.value.staff?.othersFr
  } else {
    solo = work.value.staff?.solo
    others = work.value.staff?.others
  }
  return [
    {
      key: t('piece.solo'),
      value: solo,
    },
    {
      key: '',
      value: others,
    },
  ]
})
const generalInformation = computed<DescriptionList>(() => {
  const result = [
    {
      key: t('piece.composition_date'),
      value: compositionDate.value,
    },
  ]
  if (work.value.revisions) {
    result.push({
      key: t('piece.revision_date'),
      value: work.value.revisions,
    })
  }
  if (work.value.duration && work.value.durationMax) {
    result.push({
      key: t('piece.duration'),
      value: t('labels.between', {
        duration: fmtMSS(work.value.duration),
        durationMax: fmtMSS(work.value.durationMax),
      }),
    })
  } else if (work.value.duration) {
    result.push({
      key: t('piece.duration'),
      value: fmtMSS(work.value.duration),
    })
  } else {
    result.push({
      key: t('piece.duration'),
      value: null,
    })
  }
  result.push({
    key: t('piece.editor'),
    value: work.value.editor,
  })
  result.push({
    key: 'Cycle',
    value: work.value.cycle,
  })
  result.push({
    key: t('piece.dedicatee'),
    value: work.value.dedicatee,
  })
  result.push({
    key: 'Opus',
    value: work.value.opus,
  })
  result.push({
    key: t('piece.multimedias'),
    value: work.value.multimedias,
  })
  result.push({
    key: t('piece.commanditary'),
    value: work.value.commanditary,
  })
  result.push({
    key: t('piece.libretto'),
    value: work.value.libretto,
  })
  return result
})

const informationAboutCreation = computed<DescriptionList>(() => {
  if (
    !work.value.creations ||
    !work.value.creations.length === 0 ||
    work.value.creations.filter(({ date }) => date !== '').length === 0
  ) {
    return []
  }
  return work.value.creations
    .filter(
      ({ date, location, ensemble }) =>
        date || location !== '' || ensemble !== ''
    )
    .map(({ date, location, ensemble, dateAccuracy }) => [
      {
        key: t('labels.date'),
        value: formatToFullDate(date, dateAccuracy),
      },
      {
        value: location,
      },
      {
        key: t('labels.interpreter', 2),
        value: ensemble,
      },
    ])
})

const informationAboutElectronic = computed<DescriptionList>(() => {
  if (
    !work.value.studioInformation &&
    !work.value.electronicDevice &&
    !work.value.rim
  ) {
    return []
  }
  return [
    {
      key: t('labels.information_studio'),
      value: work.value.studioInformation,
    },
    {
      key: t('labels.rim'),
      value: work.value.rim,
    },
    {
      key: t('labels.electronic_device'),
      value: work.value.electronicDevice,
    },
  ]
})

const outline = computed<SidebarMenu>(() => {
  return {
    items: [
      {
        id: 'general_information',
        resourceIcon: 'info',
        anchor: 'general_information',
        title: 'piece.general_information',
      },
      {
        id: 'type',
        resourceIcon: 'Score',
        anchor: 'type',
        title: 'piece.type',
      },
      {
        id: 'detailed_staffing',
        resourceIcon: 'Composer',
        anchor: 'detailed_staffing',
        title: 'piece.detailed_staffing',
      },
      {
        id: 'information_about_creation',
        resourceIcon: 'info',
        anchor: 'information_about_creation',
        title: 'piece.information_about_creation',
      },
      {
        id: 'information_about_electronic',
        resourceIcon: 'info',
        anchor: 'information_about_electronic',
        title: 'piece.information_about_electronic',
      },
      {
        id: 'analyses',
        resourceIcon: 'analysis',
        anchor: 'analyses',
        title: 'labels.analysis',
      },
      {
        id: 'observation',
        resourceIcon: 'notes',
        anchor: 'observations',
        title: 'piece.observations',
      },
      {
        id: 'program_note',
        resourceIcon: 'notes',
        anchor: 'program_note',
        title: 'piece.program',
      },
      {
        id: 'similar_work',
        resourceIcon: 'Analysis',
        anchor: 'similar_work',
        title: 'piece.similar_work',
      },
    ],
  }
})

const detailedStaffing = computed<string>(() => {
  return work?.value?.staff
})

const runtimeConfig = useRuntimeConfig()
const frontHost = runtimeConfig.public.FRONT_HOST
const editInformation = () => {
  window.open(`${frontHost}/ressources/works?id=${work?.value?.id}`)
}
const actions = ref<Actions>([
  {
    label: 'piece.edit_information',
    action: editInformation,
  },
])
</script>
<template>
  <DetailView :title="work.title + (compositionDate ? ' (' + compositionDate + ')' : '')" :outline="outline"
    icon-for-type="Work" :actions="actions" sticky-sidebar>
    <template #heroSubtitle>
      <div v-if="work.description">
        <span v-if="locale === 'fr'" v-html="work.description" />
        <span v-else v-html="work.descriptionEn" />
      </div>
      <h3>
        <i18n-t v-for="composer in work.composers" :key="`${composer.firstName} ${composer.lastName}`"
          keypath="labels.by_detail">
          <template #name>
            <BasicLink :to="'/composer/' + composer.slug + '/biography'">
              {{ composer.firstName }} {{ composer.lastName }}
            </BasicLink>
            <span v-if="composer.birthDate">
              ({{ parseISO(composer.birthDate).getFullYear()
              }}<span v-if="composer.deathDate">-{{ parseISO(composer.deathDate).getFullYear() }}</span>)</span>
          </template>
        </i18n-t>
      </h3>
    </template>
    <template #heroMisc>
      <div class="mt-2 flex flex-wrap gap-2 md:mt-4">
        <DataTag v-for="tag in work.tags" :key="tag.label" :tag="tag" />
      </div>
    </template>
    <a id="general_information" class="pt-40"></a>
    <h3 id="general_information" class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-0 md:text-xl">
      {{ $t('piece.general_information') }}
    </h3>
    <DataDescriptionList class="mt-4" :items="generalInformation" />
    <a id="type" class="pt-32"></a>
    <h3 id="type" class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl" v-if="work.kind">
      {{ $t('piece.type') }}
    </h3>
    <DataProse class="mt-4" text-sans>
      <template #default="{ classes }">
        <div v-if="locale === 'fr'" :class="classes" v-html="work.kind"></div>
        <div v-else :class="classes" v-html="work.kindEn"></div>
      </template>
    </DataProse>
    <a id="detailed_staffing" class="pt-32"></a>
    <h3 v-if="
      detailedStaffing && (detailedStaffing.solo || detailedStaffing.others)
    " class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
      {{ $t('piece.detailed_staffing') }}
    </h3>
    <DataDescriptionList class="mt-4" :items="detailedstaff.slice(0, 1)" />
    <p class="text-mobile-lg md:text-lg basis-36 sm:py-2 sm:pr-2 lg:basis-48 xl:basis-60">
      {{ detailedstaff[1].value }}
    </p>
    <a id="information_about_creation" class="pt-32"></a>
    <h3 v-if="informationAboutCreation && informationAboutCreation.length > 0"
      class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
      {{ $t('piece.information_about_creation') }}
    </h3>
    <div v-if="informationAboutCreation && informationAboutCreation.length > 0">
      <div v-for="(creation, index) in informationAboutCreation">
        <hr class="my-4 bg-gray-500 border-0 h-0.5" v-if="index > 0" />
        <DataDescriptionList class="mt-4" :items="creation" />
      </div>
    </div>
    <a id="information_about_electronic" class="pt-32"></a>
    <h3 v-if="informationAboutElectronic && informationAboutElectronic.length > 0"
      class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
      {{ $t('piece.information_about_electronic') }}
    </h3>
    <DataDescriptionList v-if="informationAboutElectronic && informationAboutElectronic.length > 0" class="mt-4"
      :items="informationAboutElectronic" />

    <a id="sidneyVersions" class="pt-32"></a>
    <h3 v-if="work.sidneyVersions?.length > 0"
      class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
      {{ $t('piece.versions') }}
    </h3>
    <ListResource v-if="work.sidneyVersions?.length > 0" v-for="(result, i) in work.sidneyVersions.map((version) => ({
      ...version,
      type: 'sidney',
    }))" :key="i" :resource="result" class="py-4 first:mt-4 md:p-4 md:first:mt-0" />
    <a id="analyses" class="pt-32"></a>
    <h3 v-if="work.analyses?.length > 0"
      class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
      {{ $t('labels.analysis') }}
    </h3>
    <ListRow v-for="item in work.analyses" :key="item.id" class="cursor-pointer" @click="goToResource(item)">
      <template #leftIcon>
        <img :src="item.image?.getUrl" :alt="item.title" class="h-24 w-24" />
      </template>
      <h4 v-html="item.title"
        class="text-mobile-lg font-semibold transition-colors duration-200 ease-in-out first-letter:uppercase group-hover:text-primary-400 md:text-lg" />
      <div class="flex">
        <span class="first-letter:uppercase">{{ $t('labels.by') }}&nbsp;</span>
        <div v-for="(person, index) in item.cardPersons?.slice(0, 3)">
          <p class="transition-colors duration-200 ease-in-out hover:text-primary-100">
            {{ person.firstName }} {{ person.lastName
            }}<span v-if="index !== item.cardPersons.length - 1">,&nbsp;</span>
          </p>
        </div>
      </div>
    </ListRow>
    <a id="observation" class="pt-32"></a>
    <h3 v-if="work.observation" class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
      {{ $t('piece.observations') }}
    </h3>
    <DataProse v-if="work.observation" class="mt-4" text-sans>
      <template #default="{ classes }">
        <div :class="classes" v-html="work.observation" @click="handleHtmlClick"></div>
      </template>
    </DataProse>
    <a id="program_note" class="pt-32"></a>
    <h3 v-if="work.program" class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
      {{ $t('piece.program') }}
    </h3>

    <DataProse v-if="work.program" class="mt-4" text-sans>
      <template #default="{ classes }">
        <div :class="classes" v-html="work.program" @click="handleHtmlClick"></div>
        <br />
        <br />
        <div :class="classes" v-if="work.programAuthor" v-html="work.programAuthor"></div>
      </template>
    </DataProse>
    <hr v-if="work.documents" class="mt-6 border-shade-200 md:mt-10" />
    <div v-if="work.documents">
      <h4 class="text-xl font-semibold my-2">Documents</h4>
      <ul class="list-disc px-6 text-lg">
        <li v-for="document in work.documents">
          <BasicLink :key="`${document.title}`" :to="`/numericDocument/${document.slug}`">
            {{ document.title }}
          </BasicLink>

          <span v-if="document.publishingStartDate">, le {{ formatToFullDate(document.publishingStartDate) }}.</span>
        </li>
      </ul>
    </div>
    <h3 v-if="work?.parts" class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
      {{ $t('piece.titles') }}
    </h3>
    <DataProse v-if="work?.parts" class="mt-4" text-sans>
      <template #default="{ classes }">
        <div :class="classes" v-html="work.parts"></div>
      </template>
    </DataProse>
    <hr class="my-6" v-if="work.medias?.length > 0" />
    <a id="media" class="pt-32"></a>
    <h3 v-if="work.medias?.length > 0"
      class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-6 md:text-xl">
      {{ $t('piece.recordings') }}
    </h3>
    <div v-for="video in work.medias
      .filter((m) => m)
      .filter((m) => m.type === 'video')
      .map((m) => ({ ...m, work }))" :key="video.id">
      <div>
        <MediaVideo :show-link="true" :video="video" />
        <div class="my-4 flex gap-3 md:mt-6 md:items-center" v-if="video.isExtract">
          <BasicIcon type="info" class="h-6 w-6 text-primary-400" />
          <p>
            {{ $t('descriptions.media_extract') }}
          </p>
        </div>
      </div>
    </div>
    <div v-for="audio in work.medias
      .filter((m) => m)
      .filter((m) => m.type === 'audio')
      .map((m) => ({ ...m, work }))" :key="audio.id">
      <div>
        <MediaAudio :show-link="true" :audio="audio" class="mt-4" />
        <div class="my-4 flex gap-3 md:mt-6 md:items-center" v-if="audio.isExtract">
          <BasicIcon type="info" class="h-6 w-6 text-primary-400" />
          <p>
            {{ $t('descriptions.media_extract') }}
          </p>
        </div>
      </div>
    </div>
    <template #contentMisc>
      <hr class="mt-6 border-shade-200 md:mt-10" />
      <a id="similar_work" class="pt-32"></a>
      <h3 class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:text-xl">
        {{ $t('piece.similar_work', 2) }}
      </h3>
      <div class="mt-4 flex flex-wrap gap-2 md:gap-4">
        <DataPill v-if="work.kindSlug" class="cursor-pointer" selected>
          <router-link :to="'/work/genre/' + work.kindSlug">
            {{ $t('similar_buttons.kind') }}</router-link>
        </DataPill>
        <DataPill v-if="work.creations?.[0].date" class="cursor-pointer" selected>
          <router-link :to="'/work/creation-year/' +
            new Date(work.creations[0].date).getFullYear()
            ">
            {{ $t('similar_buttons.creation_year') }}</router-link>
        </DataPill>
        <DataPill v-if="work.publishingStartDate" class="cursor-pointer" selected>
          <router-link :to="'/work/composition-year/' +
            new Date(work.publishingStartDate).getFullYear()
            ">
            {{ $t('similar_buttons.composition_year') }}</router-link>
        </DataPill>
        <DataPill class="cursor-pointer" selected>
          <router-link :to="'/search_staff?genre=' +
            JSON.stringify({
              genres: work.genreId,
              withInstruments: work.staffSlug?.split(','),
            })
            ">
            {{ $t('similar_buttons.headcount') }}</router-link>
        </DataPill>
      </div>
      <div class="mt-6 grid grid-cols-resource-card gap-6 md:mt-10 md:gap-14">
        <CardResource v-for="card in work.simili.slice(0, 2)" :key="card.id" :resource="card" />
      </div>
      <hr class="mt-6 border-shade-200 md:mt-10" />
      <a id="recommendations" class="pt-32"></a>
      <h3 v-if="work.relatedContent && work.relatedContent.length > 0"
        class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-xl">
        {{ $t('labels.recommendations') }}
      </h3>
      <div v-if="work.relatedContent && work.relatedContent.length > 0"
        class="mt-6 grid grid-cols-resource-card gap-6 md:mt-10 md:gap-14">
        <CardResource v-for="card in work.relatedContent.slice(0, 2)" :key="card.id" :resource="card" />
      </div>
      <p class="mt-4 text-base">
        {{ $t('piece.footer_text') }}
      </p>
    </template>
  </DetailView>
</template>
